import axios from 'axios';

// The auth strategy for this app requires the token to existing on a global variable

// Note: we use window.sidekick_defaults && window.sidekick_defaults.serverAccessToken instead of Sidekick.settings
// because window.sidekick_defaults && window.sidekick_defaults.serverAccessToken is immediately available on the window
// due to the sidekick application template. This now is stored in sidekick_defaults object read in from the app_icon element.
// Note: window.sidekick_defaults{billing, sidekick} has been encoded by the wineapp application_helper and stored in the app_icon element.
//       Can be accessed using Sidekick.decode(path) to get the decoded value.

// In client-to-sidekick communication, user auth is handled explicitly
// by passing the user's serverAccessToken along with each request.
const sidekick = axios.create({
  baseURL: process.env.SIDEKICK_URL,
  responseType: 'json',

  headers: {
    'X-Auth-Token': Sidekick.decode('sidekick.serverAccessToken') || '',
  },
});

// In client-to-origin communication, user auth is handled explicitly
// by passing the user's serverAccessToken along with each request.
// The origin server will read this token, and pass it to sidekick

const origin = axios.create({
  baseURL: process.env.ORIGIN_URL,
  responseType: 'json',

  headers: {
    'X-Sidekick-Token': Sidekick.decode('sidekick.serverAccessToken') || '',
  },
});

const getOrderEndpoint = (order) => {
  const { skId, type } = order;
  switch (type) {
    case 'delivery':
    case 'crossdock':
    case 'crossdock_delivery':
      return `deliveries/${skId}`;
    case 'pickup':
    case 'crossdock_pickup':
      return `pickups/${skId}`;
    case 'transfer':
      return `transfers/${skId}`;
  }
};

// camelCase → snake_case for wineapp URL params. Wineapp's
// Api::V1::Warehouse::ReportsController normalizes both camel and snake on
// the :template segment, but we send snake to match wineapp URL conventions
// and keep the migration cleanly directional.
const snakeCase = (s) => s.replace(/([A-Z])/g, '_$1').toLowerCase();

export const downloadShippingLabelsUrl = (order) => {
  const endpoint = getOrderEndpoint(order);
  return `${process.env.SIDEKICK_URL}/api/v1/${endpoint}/trackers`;
};

// Links into wineapp's /#jobs page (served at SIDEKICK_URL — the same host the
// report API calls go to). Used by the report popovers to point the warehouse
// at a freshly-requested job (the list) or an already-running one (its show page).
export const jobsUrl = () => `${process.env.SIDEKICK_URL}/#jobs`;
export const jobUrl = (jobReceiptId) =>
  `${process.env.SIDEKICK_URL}/#jobs/${jobReceiptId}`;

// POST a label/report request to wineapp's job system instead of downloading
// synchronously. Returns the job receipt JSON:
//   { job_receipt_id, status, job_name, existing }
// `existing: true` means an equivalent job was already pending/processing for
// this user — wineapp linked us to it rather than starting a duplicate.
export const requestReport = async ({
  report,
  template,
  pickDate,
  account,
  orderSkId,
  waveId,
}) => {
  const query = new URLSearchParams();
  if (pickDate) query.set('pick_date', pickDate);
  if (account) query.set('account', account);
  if (orderSkId) query.set('order_sk_id', orderSkId);
  if (waveId) query.set('wave_id', waveId);

  const { data } = await sidekick.post(
    `api/v1/warehouse/reports/${report}/${snakeCase(
      template
    )}/request?${query.toString()}`
  );
  return data;
};

export const requestBOLBundle = async (data) => {
  try {
    const { status, data: body } = await sidekick.post(
      'api/v1/bol_bundle.pdf',
      data
    );

    // 202 Accepted = newly enqueued; 200 = an equivalent bundle is already
    // running (wineapp returns its receipt rather than enqueuing a duplicate).
    if (status === 200 || status === 202) {
      return {
        success: true,
        existing: !!(body && body.existing),
        jobReceiptId: body && body.job_receipt_id,
      };
    }
    return { success: false };
  } catch (e) {
    return { success: false };
  }
};

export const updateOrder = async (skId, data) => {
  try {
    const { status } = await sidekick.patch(
      `api/v1/deliveries/${skId}.json`,
      data
    );
    if (status == 204) {
      return {
        success: true,
        message: 'Order updated successfully.',
      };
    } else {
      return {
        success: false,
        message: 'Order update failed. Try again.',
      };
    }
  } catch (e) {
    return {
      success: false,
      message: 'Order update failed. Try again.',
    };
  }
};

export const rolloverOrder = async ({
  orderId,
  userId,
  deliveryOn,
  reason,
  note,
}) => {
  try {
    const { data } = await sidekick.post('api/v1/rollovers.json', {
      rollover: {
        order_id: orderId,
        user_id: userId,
        delivery_on: deliveryOn,
        reason,
        note,
      },
    });
    return { success: true, data };
  } catch (e) {
    if (e.response && e.response.data && e.response.data.errors) {
      return {
        success: false,
        message: formatSidekickErrors(e.response.data.errors),
      };
    }
    return {
      success: false,
      message: 'Rollover failed. Please try again.',
    };
  }
};

export const createCommonCarrierTracker = async (order, carrier, code) => {
  try {
    const endpoint = getOrderEndpoint(order);
    const { data } = await sidekick.post(`api/v1/${endpoint}/trackers.json`, {
      type: carrier,
      code: code,
    });
    return {
      success: true,
      message: 'Tracker created successfully.',
      data,
    };
  } catch (e) {
    if (e.response && e.response.data && e.response.data.errors) {
      return {
        success: false,
        message: formatSidekickErrors(e.response.data.errors),
      };
    } else {
      return {
        success: false,
        message: 'A server error has occurred. Please try again later.',
      };
    }
  }
};

// getAccountsFromConfigDcList removed: accounts now derived from orders' dispatchTrackDistributionCenter

function formatSidekickErrors(errors) {
  let string = '';
  for (let [prop, messages] of Object.entries(errors)) {
    string += messages.map((message) => `${prop}: ${message}`).join('\n');
  }
  return string;
}



// WEBPACK FOOTER //
// ./src/api.js